/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BellIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { notificationService } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';

interface Notification {
  id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
  created_at: string;
  timestamp?: string;
}

interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
}

const categoryColors: Record<string, string> = {
  resources: 'bg-blue-500/20 text-blue-400',
  billing: 'bg-green-500/20 text-green-400',
  security: 'bg-red-500/20 text-red-400',
  updates: 'bg-purple-500/20 text-purple-400',
};

const categoryLabels: Record<string, string> = {
  resources: 'Resources',
  billing: 'Billing',
  security: 'Security',
  updates: 'Updates',
};

const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [realtimeNotifications, setRealtimeNotifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Generate a deduplication key for a notification
  const getDedupeKey = useCallback((n: Notification) => {
    return `${n.title}|${n.message}`;
  }, []);

  // Handle incoming WebSocket notifications
  const handleWebSocketNotification = useCallback((notification: Notification) => {
    const newDedupeKey = getDedupeKey(notification);

    setRealtimeNotifications(prev => {
      // Check for duplicates by ID or by content (title + message)
      const exists = prev.some(n =>
        n.id === notification.id || getDedupeKey(n) === newDedupeKey
      );
      if (exists) return prev;

      // Add to the beginning of the list
      return [notification, ...prev].slice(0, 50); // Keep max 50 notifications
    });

    // Refresh VM list so status (e.g. Creating -> Running) updates in the UI
    queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
  }, [getDedupeKey, queryClient]);

  // Connect to WebSocket for real-time notifications
  const { isConnected } = useNotificationWebSocket(handleWebSocketNotification);

  // Fetch notifications from API (for initial load and persisted notifications)
  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      try {
        const response = await notificationService.getAll();
        return response.data as NotificationsResponse;
      } catch {
        return { notifications: [], unread_count: 0 };
      }
    },
    refetchInterval: isConnected ? false : 30000, // Only poll if WebSocket is not connected
    retry: false,
  });

  // Merge API notifications with realtime notifications
  const allNotifications = React.useMemo(() => {
    const apiNotifications = data?.notifications || [];
    const merged = [...realtimeNotifications];
    const seenKeys = new Set<string>();

    // Track keys from realtime notifications
    for (const n of realtimeNotifications) {
      seenKeys.add(n.id);
      seenKeys.add(`${n.title}|${n.message}`);
    }

    // Add API notifications that aren't already in realtime list
    for (const notif of apiNotifications) {
      const contentKey = `${notif.title}|${notif.message}`;
      // Check both ID and content to avoid duplicates
      if (!seenKeys.has(notif.id) && !seenKeys.has(contentKey)) {
        merged.push(notif);
        seenKeys.add(notif.id);
        seenKeys.add(contentKey);
      }
    }

    // Sort by timestamp/created_at, newest first
    return merged.sort((a, b) => {
      const timeA = new Date(a.timestamp || a.created_at).getTime();
      const timeB = new Date(b.timestamp || b.created_at).getTime();
      return timeB - timeA;
    });
  }, [realtimeNotifications, data?.notifications]);

  // Calculate unread count
  const unreadCount = allNotifications.filter(n => !n.is_read).length;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: (_, id) => {
      // Update local state
      setRealtimeNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      // Update local state
      setRealtimeNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Delete notification mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => notificationService.delete(id),
    onSuccess: (_, id) => {
      // Remove from local state
      setRealtimeNotifications(prev => prev.filter(n => n.id !== id));
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.link) {
      window.location.href = notification.link;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-400 hover:text-primary-500 hover:bg-gray-800 rounded-lg transition-colors relative"
        title="Notifications"
      >
        {unreadCount > 0 ? (
          <BellIconSolid className="w-5 h-5 text-primary-500" />
        ) : (
          <BellIcon className="w-5 h-5" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-primary-500 rounded-full flex items-center justify-center">
            <span className="text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </span>
        )}
        {/* WebSocket connection indicator */}
        {isConnected && (
          <span className="absolute bottom-0.5 right-0.5 w-2 h-2 bg-green-500 rounded-full border border-gray-900"></span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              {isConnected && (
                <span className="flex items-center gap-1 text-[10px] text-green-400">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  Live
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsReadMutation.mutate()}
                className="text-xs text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
                disabled={markAllAsReadMutation.isPending}
              >
                <CheckIcon className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && allNotifications.length === 0 ? (
              <div className="p-8 flex justify-center">
                <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              </div>
            ) : allNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellIcon className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications</p>
                <p className="text-xs text-gray-500 mt-1">
                  You're all caught up!
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {allNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 hover:bg-gray-700/50 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-gray-700/30' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread indicator */}
                      <div className="mt-1.5 flex-shrink-0">
                        {!notification.is_read ? (
                          <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                        ) : (
                          <div className="w-2 h-2"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                              categoryColors[notification.category] || 'bg-gray-600 text-gray-300'
                            }`}
                          >
                            {categoryLabels[notification.category] || notification.category}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            {formatTime(notification.timestamp || notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-white truncate">
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-400 line-clamp-2">
                          {notification.message}
                        </p>
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMutation.mutate(notification.id);
                        }}
                        className="p-1 text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                        title="Delete notification"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {allNotifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-700 bg-gray-800/50">
              <a
                href="/portal/settings/notifications"
                className="text-xs text-gray-400 hover:text-primary-400 transition-colors"
              >
                Manage notification preferences
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
